import React, {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {useLanguage} from '../contexts/LanguageContext';
import {useScrollZoom} from '../hooks/useScrollZoom';
import {firestoreService} from '../firebase/firestore';
import {TagDisplay} from './admin/forms/TagSelector';
import room1 from '../assets/room_1.jpg';
import room2 from '../assets/room_2.jpg';
import room3 from '../assets/room_3.jpg';

const RoomsSection = styled.section`
  padding: 5rem 0;

  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--brand-primary);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;

  @media (min-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const RoomCard = styled.div`
  background: var(--bg-elevated);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px var(--shadow);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    transform: translateY(-10px) perspective(1000px) rotateX(2deg);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const RoomImageContainer = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;

  @media (max-width: 480px) {
    height: 200px;
  }
`;

const RoomImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${RoomCard}:hover & {
    transform: scale(1.05);
  }
`;

const RoomOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--brand-shadow-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${RoomCard}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    position: static;
    background: none;
    opacity: 1;
    height: auto;
    padding: 0 1.5rem 1.5rem;
  }
`;

const RoomBtn = styled.button`
  background: var(--bg-primary);
  color: var(--brand-primary);
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;

  &:hover {
    background: var(--bg-secondary);
  }

  @media (max-width: 768px) {
    width: 100%;
    background: var(--brand-primary);
    color: var(--text-inverse);
    padding: 0.7rem 1.5rem;

    &:hover {
      background: var(--brand-secondary);
    }
  }
`;

const CapacityBadge = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: var(--brand-shadow-strong);
  color: white;
  padding: 0.5rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  i {
    font-size: 0.8rem;
  }
`;

const RoomInfo = styled.div`
  padding: 1.5rem;
`;

const RoomTitle = styled.h3`
  color: var(--brand-primary);
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
`;

const RoomCode = styled.span`
  display: inline-block;
  background: var(--brand-light);
  color: var(--brand-primary);
  padding: 0.15rem 0.6rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 0.5rem;
  vertical-align: middle;
`;

const RoomDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1rem;
`;

const RoomFeatures = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FeatureTag = styled.span`
  background: var(--brand-light);
  color: var(--brand-primary);
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;

  i {
    font-size: 0.8rem;
  }
`;

const BuildingSection = styled.div`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BuildingHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--brand-light);
`;

const BuildingTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--brand-primary);
  margin-bottom: 0.3rem;

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const BuildingDescription = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  background: var(--bg-elevated);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px var(--shadow);
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, var(--bg-secondary) 25%, var(--bg-elevated) 50%, var(--bg-secondary) 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: ${props => props.$radius || '4px'};
  height: ${props => props.$height || '16px'};
  width: ${props => props.$width || '100%'};
`;

const SkeletonRoomCard = () => (
  <SkeletonCard>
    <SkeletonBlock $height="250px" $radius="0" />
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <SkeletonBlock $height="24px" $width="70%" />
      <SkeletonBlock $height="14px" $width="100%" />
      <SkeletonBlock $height="14px" $width="60%" />
      <SkeletonBlock $height="32px" $width="40%" $radius="15px" />
    </div>
  </SkeletonCard>
);

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    display: block;
    opacity: 0.4;
  }

  p {
    font-size: 1.1rem;
  }
`;

const Rooms = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollZoom = useScrollZoom({ disabled: reducedMotion });

  const defaultRooms = [
    {
      id: 1,
      image: room1,
      name: { en: "Cozy Mountain Room", vi: "Phòng núi ấm cúng" },
      description: { en: "Perfect for couples, featuring mountain views and modern amenities", vi: "Hoàn hảo cho các cặp đôi, có view núi và tiện nghi hiện đại" },
      beds: { en: "1 Double Bed", vi: "1 Giường đôi" },
      guests: { en: "2 Guests", vi: "2 Khách" },
      tags: ["popular", "romantic"],
      order: 0
    },
    {
      id: 2,
      image: room2,
      name: { en: "Family Suite", vi: "Phòng gia đình" },
      description: { en: "Spacious room ideal for families with children", vi: "Phòng rộng rãi lý tưởng cho gia đình có trẻ em" },
      beds: { en: "2 Beds", vi: "2 Giường" },
      guests: { en: "4 Guests", vi: "4 Khách" },
      tags: ["family", "budget"],
      order: 1
    },
    {
      id: 3,
      image: room3,
      name: { en: "Premium Suite", vi: "Phòng cao cấp" },
      description: { en: "Our most luxurious room with panoramic mountain views", vi: "Phòng sang trọng nhất với view núi toàn cảnh" },
      beds: { en: "King Bed", vi: "Giường King" },
      guests: { en: "2 Guests", vi: "2 Khách" },
      tags: ["bestseller", "luxury", "featured"],
      order: 2
    }
  ];

  const getBilingualText = (textObj, fallback = '') => {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj !== null) {
      return textObj[currentLanguage] || textObj.en || textObj.vi || fallback;
    }
    return fallback;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [firestoreRooms, buildingsData] = await Promise.all([
          firestoreService.getAll('rooms'),
          firestoreService.getAll('buildings'),
        ]);
        if (firestoreRooms && firestoreRooms.length > 0) {
          setRooms(firestoreRooms
            .filter(room => room.order !== -1)
            .sort((a, b) => (a.order || 0) - (b.order || 0)));
        } else {
          setRooms(defaultRooms.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
        setBuildings((buildingsData || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch (error) {
        console.error('Error loading rooms:', error);
        setRooms(defaultRooms);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleRoomClick = (roomId) => {
    navigate(`/booking?room=${roomId}`);
  };

  if (loading) {
    return (
      <RoomsSection id="rooms">
        <Container>
          <SectionHeader>
            <SectionTitle>{t("Our Comfortable Rooms", "Phòng nghỉ thoải mái")}</SectionTitle>
            <SectionSubtitle>{t("Choose from our beautifully designed rooms", "Lựa chọn từ những căn phòng được thiết kế đẹp mắt")}</SectionSubtitle>
          </SectionHeader>
          <RoomsGrid>
            {[1, 2, 3].map(i => <SkeletonRoomCard key={i} />)}
          </RoomsGrid>
        </Container>
      </RoomsSection>
    );
  }

  const renderRoomCard = (room, index) => {
    const roomTitle = getBilingualText(room.name || room.title, 'Room');
    const roomDescription = getBilingualText(room.description, '');
    const roomBeds = getBilingualText(room.beds, '');
    const roomGuests = getBilingualText(room.guests, '') || (room.capacity ? `${room.capacity} ${t("Guests", "Khách")}` : '');
    const roomImage = room.imageUrl || room.image || room1;

    return (
      <RoomCard key={room.id || index} data-aos="fade-up" data-aos-delay={index * 100}>
        <RoomImageContainer>
          <RoomImage src={roomImage} alt={roomTitle} loading="lazy" />
          {roomGuests && (
            <CapacityBadge>
              <i className="fas fa-users"></i>
              {roomGuests}
            </CapacityBadge>
          )}
          <RoomOverlay>
            <RoomBtn onClick={() => handleRoomClick(room.id)}>
              {t("Book Now", "Đặt phòng")}
            </RoomBtn>
          </RoomOverlay>
        </RoomImageContainer>
        <RoomInfo>
          <RoomTitle>
            {roomTitle}

          </RoomTitle>
          {room.tags && room.tags.length > 0 && (
            <TagDisplay tags={room.tags} type="room" />
          )}
          <RoomDescription>{roomDescription}</RoomDescription>
          <RoomFeatures>
            {roomBeds && (
              <FeatureTag>
                <i className="fas fa-bed"></i>
                {roomBeds}
              </FeatureTag>
            )}
          </RoomFeatures>
        </RoomInfo>
      </RoomCard>
    );
  };

  // Group rooms by building
  const hasBuildings = buildings.length > 0 && rooms.some(r => r.buildingId);
  const buildingGroups = hasBuildings
    ? buildings
        .map(building => ({
          building,
          rooms: rooms.filter(r => r.buildingId === building.id),
        }))
        .filter(group => group.rooms.length > 0)
    : [];
  const unassignedRooms = hasBuildings ? rooms.filter(r => !r.buildingId) : [];

  return (
    <RoomsSection id="rooms">
      <Container>
        <SectionHeader data-aos="fade-up" ref={scrollZoom.ref} style={scrollZoom.style}>
          <SectionTitle>
            {t("Our Comfortable Rooms", "Phòng nghỉ thoải mái")}
          </SectionTitle>
          <SectionSubtitle>
            {t("Choose from our beautifully designed rooms", "Lựa chọn từ những căn phòng được thiết kế đẹp mắt")}
          </SectionSubtitle>
        </SectionHeader>

        {rooms.length === 0 ? (
          <EmptyState>
            <i className="fas fa-bed"></i>
            <p>{t("No rooms available at the moment. Please check back soon!", "Hiện chưa có phòng. Vui lòng quay lại sau!")}</p>
          </EmptyState>
        ) : hasBuildings && buildingGroups.length > 1 ? (
          <>
            {buildingGroups.map(({ building, rooms: buildingRooms }) => (
              <BuildingSection key={building.id}>
                <BuildingHeader data-aos="fade-up">
                  <BuildingTitle>{getBilingualText(building.name, 'Building')}</BuildingTitle>
                  {building.description && (
                    <BuildingDescription>{getBilingualText(building.description, '')}</BuildingDescription>
                  )}
                </BuildingHeader>
                <RoomsGrid>
                  {buildingRooms.map((room, index) => renderRoomCard(room, index))}
                </RoomsGrid>
              </BuildingSection>
            ))}
            {unassignedRooms.length > 0 && (
              <BuildingSection>
                <RoomsGrid>
                  {unassignedRooms.map((room, index) => renderRoomCard(room, index))}
                </RoomsGrid>
              </BuildingSection>
            )}
          </>
        ) : (
          <RoomsGrid>
            {rooms.map((room, index) => renderRoomCard(room, index))}
          </RoomsGrid>
        )}
      </Container>
    </RoomsSection>
  );
};

export default Rooms;